import { useEffect, useRef, type ReactNode } from 'react';

// Bars scale to at least this so a thin sliver stays visible during silence.
const VISIBILITY_FLOOR = 0.04;

// 256-point FFT → 128 frequency bins. this value has to be a power of 2.
const FFT_SIZE = 256;

// Smaller = more responsive bars.
const MIC_SENSITIVITY = 420; // nice 🥬

const NUMBER_OF_BARS = 56;

type Source = {
    tick: () => (halfIndex: number) => number;
    dispose: () => void;
};

async function createMicSource(halfBars: number): Promise<Source> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = FFT_SIZE;
    audioContext.createMediaStreamSource(stream).connect(analyser);

    const frequencyData = new Uint8Array(analyser.frequencyBinCount);
    // we are only sampling half the frequency spectrum, so from 0-24kHz~, since
    // everything else is inaudible.
    const voiceRangeBins = Math.floor(frequencyData.length / 2);

    return {
        // this function is called on every render to update the frequency data.
        // returns a function that will accept a bar index, and returns a frequency value.
        // it mutates the frequencyData array on each render. it will look like:
        // [0, 0, 0, ...., 0] - initial state
        // [178, 165, 8, ..., 2] - first render
        // [201, 188, 57, ...., 0] - second render
        // the value will be between 0-255.
        // the array size will be FFT_SIZE / 2, which represents how many
        // slices of the frequency spectrum we will have. beware that this
        // is not the same as the frequency bars in the frontend, these need
        // to be mapped from one another.
        tick: () => {
            analyser.getByteFrequencyData(frequencyData);

            return (halfIndex) => {
                // Map bar position to frequency value position.
                // Rescale halfIndex from [0, halfBars] into [0, voiceRangeBins].
                const bin = Math.floor((halfIndex / halfBars) * voiceRangeBins);

                return (frequencyData[bin] ?? 0) / MIC_SENSITIVITY;
            };
        },
        dispose: () => {
            for (const track of stream.getTracks()) {
                track.stop();
            }

            void audioContext.close();
        },
    };
}

export function Waveform(): ReactNode {
    const refs = useRef<(HTMLSpanElement | null)[]>([]);

    // Flow:
    //   1. Try to open a mic source (async `createMicSource`). While waiting, bars sit at floor.
    //   2. If that's successful, `start` installs the source and kicks off the requestAnimationFrame loop.
    //   3. Each `frame` paints all bars, then schedules the next `frame`
    //      via requestAnimationFrame - a self-scheduling loop that only
    //      stops when cleanup calls cancelAnimationFrame(raf).
    useEffect(() => {
        const half = NUMBER_OF_BARS / 2;
        let raf = 0;
        let cancelled = false;
        let source: Source | null = null;

        const setBarHeight = (barIndex: number, value: number): void => {
            const el = refs.current[barIndex];

            if (el) {
                el.style.transform = `scaleY(${Math.max(VISIBILITY_FLOOR, Math.min(1, value))})`;
            }
        };

        // one paint pass, then schedules the next frame. This is the animation loop - the recursion is via requestAnimationFrame.
        const frame = (): void => {
            if (!source) {
                return;
            }

            const getBarValue = source.tick();

            // Compute half the bars; each value paints its mirror on the other side.
            // This is an aesthetic choice: most voice frequency visualizers mirror the spectrum because a voice is mostly present in lower frequencies.
            for (let halfIndex = 0; halfIndex < half; halfIndex++) {
                const value = getBarValue(halfIndex);
                setBarHeight(half + halfIndex, value); // right of center
                setBarHeight(half - 1 - halfIndex, value); // left of center
            }

            raf = requestAnimationFrame(frame);
        };

        void createMicSource(half)
            .then((micSource) => {
                if (cancelled) {
                    micSource.dispose();
                    return;
                }

                source = micSource;
                frame();
            })
            .catch(() => {
                // Mic unavailable/denied — leave bars at floor, no loop.
            });

        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
            source?.dispose();
        };
    }, []);

    return (
        <div className="flex h-16 items-center justify-center gap-[3px]">
            {Array.from({ length: NUMBER_OF_BARS }).map((_, barIndex) => (
                <span
                    key={barIndex}
                    ref={(el) => {
                        refs.current[barIndex] = el;
                    }}
                    className="h-12 w-[3px] origin-center rounded-full bg-blue-500"
                    style={{ transform: `scaleY(${VISIBILITY_FLOOR})` }}
                />
            ))}
        </div>
    );
}
